import { DataStorage, fabricApi } from "../../imports";
import { setFile, getCurrentFile } from "./fileCreators";
import {
  updateDimensions,
  updateNode,
  updatePlayerState,
} from "../reducers/playerSlice";
import {
  imageSize,
  multiImageNodeState,
  multiImagePlayerState,
  singleImagePlayerState,
} from "../../helpers/playerHelpers";
import { checkMultipleCanvas, getProductType } from "./threekitCreators";
import {
  hasFrontAndBack,
  isApparel,
  isArtwork,
  usesImageMask,
} from "../../helpers/productHelpers";
import productValues from "../../assets/fabricProductValues";
import {
  addProductTexture,
  addUploadIconAndRender,
  canDimensions,
  createCanvas,
  dimensionsToPixelDimensions,
  findByName,
  getCanvas,
  getCurrentCanvas,
  getFrameColor,
  getImageMask,
  getMask,
  hasFileUploaded,
  maskDimensions,
  setActiveImage,
  setOverlayImage,
  triptychImage,
  updateMaskColors,
} from "../../helpers/fabricHelpers";
import { default as triptychMetadata } from "../../../../config/triptychMetadata";
import { IMAGE_UPLOAD_ERROR } from "../../../../constants/general";

// URLs for default images saved in shopify
const loadingPicture =
  "https://cdn.shopify.com/s/files/1/0591/4299/6149/files/loadingicon.png";
const errorPicture =
  "https://cdn.shopify.com/s/files/1/0591/4299/6149/files/imageError.png";
const urlParams = new URLSearchParams(window.location.search);

// Saves canvas pointer in data storage and state
export function setCanvas(canvas, node) {
  return (dispatch) => {
    const canvasPointer = DataStorage.create(canvas);
    dispatch({
      type: "fabric/saveCanvasPointer",
      payload: { canvasPointer, node },
    });
  };
}

/**
 * This is the entry point for initializing the canvas/
 * product builder. This creates the actual fabric canvas
 * and saves its reference
 */
export function setUpFabric() {
  return async (dispatch, getState) => {
    const state = getState();
    const productType = getProductType(state);
    let node = hasFrontAndBack(productType) ? "front" : null;

    // Create fabric canvas instance
    const canvas = createCanvas("canvas");

    // Adds drop shadow to canvas if needed
    if (productValues[productType].config?.dropShadow) {
      const canvasDiv = document.getElementsByClassName("canvas-container")[0];
      canvasDiv.style.filter = productValues[productType].config.dropShadow;
    }

    // Set canvases and create masks
    dispatch(setCanvas(canvas, node));
    dispatch(createMask(node));
    hasFrontAndBack(productType) && dispatch(setBackCanvas());

    // Handles preloaded image from url params
    const imageParam = urlParams.get("imageId");
    if (imageParam) {
      node = getState().playerState.currentNode || node;
      dispatch(setExistingImage(imageParam, node));
    }

    // If a static artwork product, add artwork as file
    if (isArtwork(state.threekit.selectedProduct)) {
      const image = await fabricApi.fetchArtworkImage(
        state.threekit.selectedProduct.id
      );
      const file = await fabricApi.convertArtworkToFile(image);
      dispatch(setFile(file));
    }
  };
}

// Starts process of setting existing image from url params
export function setExistingImage(param, node) {
  return async (dispatch) => {
    dispatch({ type: "player/setCurrentNode", payload: node });
    dispatch({ type: "fabric/saveImageId", payload: { imgId: param, node } });

    let image = await fabricApi.fetchImage(param);
    let file = await fabricApi.convertToFile(image);

    dispatch(setFile(file, node));
  };
}

// Creates a second canvas for the back of apparel
export function setBackCanvas() {
  return async (dispatch) => {
    let node = "back";
    const canvasBack = createCanvas("canvasBack");
    dispatch(setCanvas(canvasBack, node));
    dispatch(createMask(node));

    // Handles setting existing image from url params
    const frontParam = urlParams.get("front");
    const backParam = urlParams.get("back");
    if (frontParam || backParam) {
      // Disable toggle
      const toggle = document.getElementById("fabricToggle");
      const switchBtn = document.getElementsByClassName("switch-button")[0];
      toggle.disabled = true;
      switchBtn.style.color = "#5e5e5e";

      // Set existing images
      frontParam && (await dispatch(setExistingImage(frontParam, "front")));
      backParam && (await dispatch(setExistingImage(backParam, "back")));

      // Reset
      toggle.disabled = false;
      switchBtn.style.color = "#ffffff";
    }

    dispatch({ type: "player/setCurrentNode", payload: "front" });
  };
}

/**
 * Create mask(s)
 * Masks represent the product's print area(s) and
 * are set as a clipPath on the canvas. Multiple
 * masks get grouped together in a fabric group and the
 * group is set as the clipPath
 */
export function createMask(node) {
  return async (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state, node);
    const productType = getProductType(state);
    canvas.clear();

    // Multiple mask render
    function renderMasks(masks) {
      const group = createMaskGroup(productType);
      canvas.clipPath = group;
      canvas.add(group);
      // Go through each mask, add to group, and handle click event
      masks.forEach((mask) => {
        group.addWithUpdate(mask);
        const onMouseUp = () => {
          dispatch({ type: "player/setCurrentNode", payload: mask.name });
          dispatch(setActiveImage(canvas, mask.name));
          // If file is already added, only trigger upload on doubleclick
          if (!getState().files.files[mask.name] || window.dblClick === true) {
            document.getElementById("threekit-fileupload").click();
          } else {
            window.dblClick = true;
            setTimeout(() => (window.dblClick = false), 400);
          }
        };
        // passing in mask as the group so it centers the image within the mask
        addUploadIconAndRender(mask, canvas, mask, onMouseUp);
      });
      dispatch({ type: "player/setCurrentNode", payload: masks[0].name });
      dispatch(setActiveImage(canvas, masks[0].name));
      canvas.centerObject(group).renderAll();
    }

    // Single mask render
    function renderMask(mask) {
      canvas.clipPath = mask;
      canvas.add(mask).centerObject(mask);

      // Mask movements to fit model
      if (productValues[productType].config?.offset) {
        const offset = productValues[productType].config?.offset;
        const top = node == "back" ? offset.backTop : offset.top;
        const left = node == "back" ? offset.backLeft : offset.left;
        top && mask.set({ top: mask.top + top }).setCoords();
        left && mask.set({ left: mask.left + left }).setCoords();
      }

      // If product has overlay texture
      if (productValues[productType].texture) {
        addProductTexture(state, canvas);
      }

      // If no file is uploaded yet, add the upload icon
      if (!hasFileUploaded(state, node)) {
        addUploadIconAndRender(mask, canvas);
      }

      canvas.renderAll();

      // Launch file select on mouseup
      mask.on("mouseup", function () {
        document.getElementById("threekit-fileupload").click();
      });
    }

    // Check to see if we need multiple masks
    const renderMultipleMasks =
      checkMultipleCanvas(state) && !hasFrontAndBack(productType);

    const urls = {
      'bottle': 'https://cdn.shopify.com/s/files/1/0591/4299/6149/files/bottleopener.png',
      'dog-tag': 'https://cdn.shopify.com/s/files/1/0591/4299/6149/files/dog-tag.webp',
      'bottle-opener-brushed': 'https://cdn.shopify.com/s/files/1/0591/4299/6149/files/opener-stainless1.png?v=1694209689',
      'koozie-16': 'https://cdn.shopify.com/s/files/1/0591/4299/6149/files/16ozKoozieMask.png?v=1694629978',
      'koozie-12': 'https://cdn.shopify.com/s/files/1/0591/4299/6149/files/12ozKoozieMask.png?v=1694630091'
    }
    // Create the mask
    usesImageMask(productType)
      ? getImageMask(canvas, urls[productType])
      : getMask(state, renderMultipleMasks ? renderMasks : renderMask);
  };
}

// Creates group of masks for products with multiple print areas
function createMaskGroup(productType) {
  const maskGroup = new fabric.Group([], {
    name: "maskGroup",
    selectable: false,
    subTargetCheck: true,
  });

  // Collage needs white background behind masks
  if (productType == "collage") {
    const background = new fabric.Rect({
      name: "background",
      width: 385,
      height: 385,
      fill: "#fff",
      selectable: false,
      absolutePositioned: true,
    });
    maskGroup.addWithUpdate(background);
  } else {
    maskGroup.set({
      width: canvas.width,
      height: canvas.height,
    });
  }
  return maskGroup;
}

// Upload and display error image and message
export function displayErrorImage(message) {
  return async (dispatch) => {
    const errorPic = await fabricApi.convertToFile(errorPicture);
    try {
      await dispatch(addImage(errorPic));
      dispatch({
        type: "fabric/setError",
        payload: { active: true, message: message },
      });
    } catch (error) {
      const oopsMessage = "Oops, something went wrong. Please try again later.";
      console.log(error)
      dispatch({
        type: "fabric/setError",
        payload: { active: true, message: oopsMessage },
      });
      return;
    }
  };
}

/**
 * Starting point for adding uploaded image. This
 * is triggered by a file upload handled in
 * fileCreators.js setFile()
 */
export function uploadImage(node) {
  return async (dispatch, getState) => {
    // Add and show loading icon
    const icon = await fabricApi.convertToFile(loadingPicture);
    try {
      await dispatch(addImage(icon, node));
    } catch {
      const message = "Oops, something went wrong. Please try again later.";
      dispatch({
        type: "fabric/setError",
        payload: { active: true, message: message },
      });
      return;
    }

    // Get state values
    const state = getState();
    const file = getCurrentFile(state, node);
    const productType = getProductType(state);

    // Disable add to cart button while processing
    dispatch({ type: "threekit/setAddToCartDisabled", payload: true });

    /**
     * Some browsers have a threshold for the size of image it can load
     * This attempts to get the image size and adds an error image
     * if it's too large to be processed
     */
    let imageSizeVal;
    try {
      imageSizeVal = await imageSize(file);
    } catch (e) {
      const message = "Your image is too large. Please resize it and try again";
      dispatch(displayErrorImage(message));
      return;
    }

    // Get image size
    let imgHeight = imageSizeVal.height;
    let imgWidth = imageSizeVal.width;

    // Upload file to API for storage and catch any upload error
    let img = null;
    let uploadMessage =
      "There was an error uploading your image. If error persist please contact customer support.";
    try {
      img = await fabricApi.handleDirectUpload(file);
    } catch (e) {
      dispatch(displayErrorImage(uploadMessage));
      return;
    }
    if (!img || img === IMAGE_UPLOAD_ERROR) {
      dispatch(displayErrorImage(uploadMessage));
      return;
    }

    // Set url and id
    const imgUrl = img.image_url;
    const imgId = img.unique_id;
    dispatch({ type: "fabric/saveImageId", payload: { imgId, node } });

    // Add image to fabric canvas
    dispatch(addImage(false, node));

    // Get and save product dimensions
    const [canWidth, canHeight] = canDimensions(state);
    if (canWidth && canHeight && productType !== "collage") {
      dispatch(updateDimensions({ canHeight, canWidth }));
    }

    // Create initial player states
    let newState = {};
    if (checkMultipleCanvas(state)) {
      newState = multiImagePlayerState(imgHeight, imgWidth);
      /**
       * Multiple image products use an array of nodes to track
       * each individual image and its movements. So each node
       * corresponds to an image/file
       */
      const nodeState = multiImageNodeState(
        state,
        canWidth,
        canHeight,
        imgHeight,
        imgWidth,
        node,
        imgUrl,
        imgId
      );
      dispatch(updateNode(nodeState));
    } else {
      newState = singleImagePlayerState(
        state,
        canWidth,
        canHeight,
        imgHeight,
        imgWidth,
        productType,
        imgUrl,
        imgId
      );
    }
    dispatch(updatePlayerState({ data: newState }));

    // Reenable add to cart button
    dispatch({ type: "threekit/setAddToCartDisabled", payload: false });
  };
}

// Add uploaded image to canvas
export function addImage(loading, node = "") {
  return (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state, node);
    const productType = getProductType(state);
    let currentMaskGroup, maskGroup, currentMask;

    /**
     * Multiple canvas products use multiple fabric masks,
     * find the correct mask to add the image to
     */
    if (checkMultipleCanvas(state) && !hasFrontAndBack(productType)) {
      maskGroup = findByName(canvas, "maskGroup");
      currentMaskGroup = findByName(maskGroup, node);
      currentMask = currentMaskGroup.getObjects()[0];

      // Remove upload icon from group
      currentMaskGroup.getObjects().filter((obj) => {
        if (!obj.name.includes("mask")) {
          currentMaskGroup.remove(obj);
        }
      });
    }

    // Get the current image file
    let file = loading ? loading : getCurrentFile(state, node);
    const fileUrl = URL.createObjectURL(file);

    // Add image to fabric builder
    if (productType.includes("triptych")) {
      dispatch(addTriptychImage(loading, fileUrl));
    } else {
      new fabric.Image.fromURL(fileUrl, (image) => {
        let heightRatio, widthRatio, name;
        // If multiple image product, there will be a current mask
        if (currentMask) {
          heightRatio = currentMask.height / image.height;
          widthRatio = currentMask.width / image.width;
          name = loading ? "loading_" + node : "img_" + node;
        } else {
          heightRatio = canvas.clipPath.height / image.height;
          widthRatio = canvas.clipPath.width / image.width;
          name = loading ? "loading" : "img";
        }
        // This ratio sizes the image properly for its containing object
        const ratio = heightRatio > widthRatio ? heightRatio : widthRatio;

        image.set({
          name,
          scaleX: ratio,
          scaleY: ratio,
          originX: "center",
          originY: "center",
          hasControls: false,
          selectable: false,
          evented: false,
          borderColor: "#000000",
        });

        if (currentMask) {
          // Remove anything that isn't the mask
          maskGroup.getObjects().filter((obj) => {
            if (obj.name.includes("_" + node)) {
              maskGroup.remove(obj);
            }
          });
          image.set({ clipPath: currentMask });
          const centerPoint = currentMaskGroup.getCenterPoint();
          image.setPositionByOrigin(centerPoint, "center", "center");
          maskGroup.add(image);
        } else {
          // Remove anything that isn't the mask
          canvas._objects.filter((obj) => {
            !obj.name.includes("mask") && canvas.remove(obj);
          });
          canvas.add(image).centerObject(image);
        }

        // Movements to match any mask offsets
        if (productValues[productType].config?.offset) {
          const offset = productValues[productType].config?.offset;
          const top = node == "back" ? offset.backTop : offset.top;
          const left = node == "back" ? offset.backLeft : offset.left;
          top && image.set({ top: image.top + top }).setCoords();
          left && image.set({ left: image.left + left }).setCoords();
        }

        canvas.setActiveObject(image).renderAll();
      });
    }
  };
}

// Handle adding images for triptychs
export function addTriptychImage(loading, fileUrl) {
  return (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state, false);
    const productType = getProductType(state);
    const imageGroup = new fabric.Group([], {
      name: "imageGroup",
      selectable: false,
    });

    // Left
    new fabric.Image.fromURL(fileUrl, async (image) => {
      const maskGroup = canvas.item(0);
      const heightRatio = productValues[productType].height / image.height;
      const widthRatio = (productValues[productType].width * 3) / image.width;
      const ratio = heightRatio > widthRatio ? heightRatio : widthRatio;
      const name = loading ? "loading" : "img_left";

      image.set(triptychImage(name, maskGroup.top, maskGroup.left, ratio));

      canvas.getObjects().filter((obj) => {
        obj.name != "maskGroup" && canvas.remove(obj);
      });

      imageGroup.addWithUpdate(image);
      image.set({ clipPath: maskGroup._objects[0] });

      // Center
      new fabric.Image.fromURL(fileUrl, (image) => {
        const name = loading ? "loading" : "img_center";
        image.set(
          triptychImage(name, maskGroup.top, maskGroup.left + 15, ratio)
        );
        imageGroup.addWithUpdate(image);
        image.set({ clipPath: maskGroup._objects[1] });

        // Right
        new fabric.Image.fromURL(fileUrl, (image) => {
          const name = loading ? "loading" : "img_right";
          image.set(
            triptychImage(name, maskGroup.top, maskGroup.left + 30, ratio)
          );
          imageGroup.addWithUpdate(image);
          image.set({ clipPath: maskGroup._objects[2] });
          canvas.add(imageGroup).centerObject(imageGroup);
        });
      });
    });
  };
}

// Change paper type texture for posters
export function changePaperType(value) {
  return (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state);
    const productType = getProductType(state);
    const texture = productValues[productType].texture[value];

    // Add overlay to canvas
    setOverlayImage(canvas, texture.source, texture.opacity);
  };
}

// Resize canvas triggered from size selector
export function resizeCanvas(canHeight, canWidth) {
  return async (dispatch, getState) => {
    const state = getState();
    const productType = getProductType(state);

    const [height, width] = dimensionsToPixelDimensions(
      [canHeight, canWidth],
      productType
    );

    // If there is a frame, clear it from the canvas
    const frameCanvas = DataStorage.find(state.fabric.frameCanvas) || null;
    frameCanvas?.clear();

    if (productType.includes("triptych")) {
      await dispatch(updateTriptychImage(canWidth, canHeight));
    } else if (
      productValues[productType].width != width ||
      productValues[productType].height != height
    ) {
      productValues[productType].width = width;
      productValues[productType].height = height;

      checkMultipleCanvas(state)
        ? await dispatch(updateMultipleImages(canWidth, canHeight))
        : await dispatch(updateImage(canWidth, canHeight));
    }
  };
}

// Update canvas triggered from changed variant options
export function updateModelOptions() {
  return async (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state);
    const productType = getProductType(state);
    const [canWidth, canHeight] = canDimensions(state);

    let canvasRatio = canvas.clipPath.height / canvas.clipPath.width;
    let displayRatio = canHeight / canWidth;

    /**
     * If new size ratio is different than existing, update the builder
     *
     * Foam tiles update due to changing the number of tiles so they need
     * to be updated every time to account for the new number
     */
    if (canvasRatio !== displayRatio || productType === "tile") {
      if (isApparel(productType) && hasFrontAndBack(productType)) {
        await dispatch(updateFrontAndBackImages(canWidth, canHeight));
      } else {
        checkMultipleCanvas(state)
          ? await dispatch(updateMultipleImages(canWidth, canHeight))
          : await dispatch(updateImage(canWidth, canHeight));
      }
      // Update apparel mask in case of color change
    } else if (isApparel(productType)) {
      updateMaskColors(state);
    }
  };
}

// Handles updating the image on canvas resizes
export function updateImage(canWidth, canHeight) {
  return async (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state);
    const productType = getProductType(state);

    // If there isn't an image added to the canvas, exit
    if (canvas.item(1) == undefined) {
      return;
    }

    // Re-create and re-add mask
    dispatch(createMask());

    // Get current information
    const { imageHeightOriginal, imageWidthOriginal } =
      state.playerState.finalObj;
    const { fileID, imageIDs } = state.playerState.imageMagickObj;

    // Update dimensions
    dispatch(updateDimensions({ canHeight, canWidth }));

    // Readd image
    dispatch(addImage(false, ""));

    // Reset player state
    const newState = singleImagePlayerState(
      state,
      canWidth,
      canHeight,
      imageHeightOriginal,
      imageWidthOriginal,
      productType,
      fileID,
      imageIDs
    );
    dispatch(updatePlayerState({ data: newState }));

    // If product has a bleed update that as well
    productValues[productType].config?.bleed && dispatch(changeBleed());
  };
}

// Handles triptych canvas resize and changes
export function updateTriptychImage(canWidth, canHeight) {
  return (dispatch, getState) => {
    const state = getState();
    const { imageHeightOriginal, imageWidthOriginal, ratio } =
      state.playerState.finalObj;

    // Update dimensions and player state
    dispatch(updateDimensions({ canHeight, canWidth }));

    // Update player state values to match new size of triptych
    const cnH = canHeight * 96;
    const cnW = canWidth * 96;
    const heightRatio = cnH / imgH;
    const widthRatio = cnW / imgW;
    const imgRatio = heightRatio > widthRatio ? heightRatio : widthRatio;
    const imgH = imageHeightOriginal * imgRatio;
    const imgW = imageWidthOriginal * imgRatio;
    const sku = state.threekit.selectedProduct.variants[0].sku;

    const newState = {
      imageMagickObj: {
        canHeight: cnH,
        canWidth: cnW,
        imageHeight: imgH * (ratio - 1) + imgH,
        imageWidth: imgW * (ratio - 1) + imgW,
      },
      finalObj: {
        canHeight: cnH,
        canWidth: cnW,
        metadata: triptychMetadata[sku],
      },
    };
    dispatch(updatePlayerState({ data: newState }));
  };
}

// Handles updates for products with front and back
export function updateFrontAndBackImages(canWidth, canHeight) {
  return async (dispatch, getState) => {
    const state = getState();
    const finalObj = state.playerState.finalObj;

    // Loop through front and back nodes
    for (const node of ["front", "back"]) {
      // Re-create and re-add mask
      dispatch(createMask(node));

      // Get current node obj
      const current = finalObj.nodes[node];

      // If node has an image, update it
      if (current && current.fileID) {
        // Readd image
        dispatch(addImage(false, node));

        // Reset player state
        const nodeState = multiImageNodeState(
          state,
          canWidth,
          canHeight,
          finalObj.imageHeightOriginal,
          finalObj.imgWidthOriginal,
          node,
          current.fileID,
          current.imgId
        );
        dispatch(updateNode(nodeState));
      }
    }
  };
}

// Handle updates for products with multiple images
export function updateMultipleImages() {
  return async (dispatch, getState) => {
    const state = getState();
    const productType = getProductType(state);
    const finalObj = state.playerState.finalObj;
    let newNodes = null;

    // Create new mask group
    dispatch(createMask());

    /**
     * Handle pack size change. Removes extra nodes and
     * files that go beyond new pack size
     */
    if (productType == "tile") {
      // Get selected pack size
      const packSize = state.threekit.selectedProduct.option1 || "5";
      newNodes = {};
      const newFiles = {};

      // Loop through existing nodes
      for (const node of Object.keys(finalObj.nodes)) {
        // If node is still within the pack size, add it
        if (Number(node.split("_")[1]) <= packSize) {
          newNodes[node] = finalObj.nodes[node];
          newFiles[node] = state.files.files[node];
        }
      }
      // If newNodes is a different length than current nodes, reset
      if (Object.keys(newNodes).length !== Object.keys(finalObj.nodes).length) {
        dispatch({ type: "player/resetNodes", payload: { data: newNodes } });
        dispatch({ type: "file/resetFiles", payload: { data: newFiles } });
      }
    }

    // Get and save product dimensions
    const [canWidth, canHeight] = canDimensions(state);
    if (canWidth && canHeight && productType !== "collage") {
      dispatch(updateDimensions({ canHeight, canWidth }));
    }

    // If newNodes exists use it
    const nodes = newNodes !== null ? newNodes : finalObj.nodes;

    // Loop through existing nodes
    for (const node of Object.keys(nodes)) {
      // Get current node object
      const current = nodes[node];

      // Re-add image
      dispatch(addImage(false, node));

      // Reset player state
      const nodeState = multiImageNodeState(
        state,
        canWidth,
        canHeight,
        finalObj.imageHeightOriginal,
        finalObj.imgWidthOriginal,
        node,
        current.fileID,
        current.imageID
      );
      dispatch(updateNode(nodeState));
    }
  };
}

/**
 * Adds a border for posters
 *
 * @param {number} bleed   Size of border
 *
 * @returns
 */
export function changeBleed(bleed) {
  return async (dispatch, getState) => {
    const state = getState();
    const selectedValue = state.threekit.options
      .find((option) => option.label == "border")
      .values.find((value) => value.selected);
    if (!bleed && bleed !== 0) {
      bleed = { "Full bleed": 0, '0.5"': 0.5, '1"': 1 }[selectedValue.value];
    }
    const { canvas } = getCanvas(state);
    // mask: rendered whitespace that represents our poster "canvas"
    const mask = canvas.getObjects()[0];
    // texture: the actual image object, as rendered in fabric
    const texture = canvas.getObjects()[1];
    // setting a Fabric object to dirty causes it to rerender when we call canvas.renderAll()
    if (texture) texture.dirty = true;
    const canWidth = parseInt(state.playerState.dimensions.canWidth);
    const pixelsPerInch = mask.width / canWidth;
    // Anything in the image outside this box will not be rendered
    const clipMask = new fabric.Rect({
      absolutePositioned: true,
    });
    // If bleed is zero, this will be the left border of the mask.
    // Otherwise it will set it further out depending on how big the bleed is.
    clipMask.left = mask.left + pixelsPerInch * bleed;
    // we use the same logic to calculate the top
    clipMask.top = mask.top + pixelsPerInch * bleed;
    // calculates how many total pixels on either side of the image should be bleed
    // used below to calculate width and height of the mask
    const pixelsToRemove = pixelsPerInch * bleed * 2;
    // ie. if there were 20 pixel per inch, and the bleed was 1 inch
    // we'd want the clipMask width to be 40 pixels less than the total rendered canvas width
    clipMask.width = mask.width - pixelsToRemove;
    clipMask.height = mask.height - pixelsToRemove;
    // give the image this clipMask
    canvas.getObjects()[1].clipPath = clipMask;
    canvas.renderAll();
  };
}

/**
 * Adds frame to single canvas in the builder
 *
 * @param {Object} frame   Current frame selection
 *
 * @returns
 */
export function changeFloaterFrame(frame) {
  return async (dispatch, getState) => {
    const state = getState();
    const productType = getProductType(state);

    // Check for existing frame canvas
    let frameCanvas = DataStorage.find(state.fabric.frameCanvas) || null;

    // If frame canvas doesn't already exist, create it
    if (frameCanvas === null) {
      frameCanvas = new fabric.Canvas("frame", {
        height: 475,
        width: 610,
        controlsAboveOverlay: true,
      });
      // Store a reference for the canvas
      dispatch({
        type: "fabric/saveFrameCanvasPointer",
        payload: DataStorage.create(frameCanvas),
      });
    }

    // Clear canvas
    frameCanvas.clear();

    // Get dropshadow value and canvas divs
    const dropShadow = productValues[productType].config?.dropShadow;
    const frameDiv = document.getElementsByClassName("canvas-container")[0];
    const canvasDiv = document.getElementsByClassName("canvas-container")[1];

    // If frame selected is none
    if (frame.value === "None") {
      // Reset dropshadow
      frameDiv.style.filter = "";
      canvasDiv.style.filter = dropShadow;

      // Exit
      return;
    }

    // Add rectangle to canvas for frame
    const [width, height] = maskDimensions(state, productType);
    const frameMask = new fabric.Rect({
      width,
      height,
      stroke: getFrameColor(frame.color),
      strokeWidth: 20,
      selectable: false,
      absolutePositioned: true,
    });
    frameCanvas.add(frameMask).centerObject(frameMask);

    // Remove dropshadow from canvas and add to frame
    frameDiv.style.filter = dropShadow;
    canvasDiv.style.filter = "";

    // Move frame to match mask offset
    if (productValues[productType].config?.offset) {
      const offset = productValues[productType].config?.offset;
      offset.top &&
        frameMask.set({ top: frameMask.top + offset.top }).setCoords();
      offset.left &&
        frameMask.set({ left: frameMask.left + offset.left }).setCoords();
    }
  };
}
